/**
 * The Neusix Sales Console dashboard.
 *
 * INTERNAL. Where an operator creates a client link and sees how every
 * Business Discovery on record is going. The figures are counted from
 * the session list the backend returns; nothing is estimated. None of it is
 * reachable from a client's link, which carries an access token and no
 * session id.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import DiscoveryPyramid from '../components/DiscoveryPyramid';
import ConsoleLayout from './ConsoleLayout';
import NewDiscoveryLink from './NewDiscoveryLink';
import { Card, CopyLinkButton, EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge, Value } from './consoleUi';
import {
  clientOf,
  companyOf,
  formatDateTime,
  greeting,
  progressOf,
  useDiscoverySessions,
} from './consoleData';
import '../styles/SessionsPage.css';

const RECENT_COUNT = 6;

function Stat({ label, value, hint }) {
  return (
    <div className="nsc-stat">
      <p className="nsc-stat__label">{label}</p>
      <p className="nsc-stat__value">{value}</p>
      {hint && <p className="nsc-stat__hint">{hint}</p>}
    </div>
  );
}

export default function SessionsPage() {
  const { sessions, discoveries, status, error, reload } = useDiscoverySessions({
    detailLimit: RECENT_COUNT,
  });

  const counts = useMemo(() => ({
    total: sessions.length,
    notStarted: sessions.filter((s) => s.lifecycle === 'created').length,
    inProgress: sessions.filter((s) => s.lifecycle === 'active').length,
    completed: sessions.filter((s) => s.lifecycle === 'completed' || s.lifecycle === 'report_generated').length,
    reports: sessions.filter((s) => s.report_generated).length,
  }), [sessions]);

  const show = (value) => (status === 'ready' ? value : status === 'loading' ? '…' : '—');
  const recent = sessions.slice(0, RECENT_COUNT);

  return (
    <ConsoleLayout title="Dashboard">
      <PageHeader
        eyebrow={greeting()}
        title="Business Discovery Overview"
        subtitle="Create client links and follow each Business Discovery from the first answer to the report."
      />

      {error && status === 'ready' && (
        <p className="nsc-alert nsc-alert--danger" role="alert">{error}</p>
      )}

      <section className="nsc-stats" aria-label="Business Discovery figures">
        <Stat
          label="Total discoveries"
          value={show(counts.total)}
          hint={status === 'ready' && counts.notStarted > 0 ? `${counts.notStarted} not started yet` : null}
        />
        <Stat label="In progress" value={show(counts.inProgress)} />
        <Stat label="Completed" value={show(counts.completed)} />
        <Stat label="Reports generated" value={show(counts.reports)} />
      </section>

      <NewDiscoveryLink onCreated={reload} />

      <div className="nsc-dashboard">
        <Card
          title="Recent Business Discoveries"
          actions={
            sessions.length > 0 && (
              <Link to="/internal/discoveries" className="nsc-button nsc-button--ghost">
                View all
              </Link>
            )
          }
          className="nsc-card--flush"
        >
          {status === 'loading' && <LoadingState label="Loading Business Discoveries…" />}
          {status === 'error' && (
            <ErrorState
              title="Business Discoveries could not be loaded"
              action={<button type="button" className="nsc-button nsc-button--secondary" onClick={reload}>Try again</button>}
            >
              {error}
            </ErrorState>
          )}
          {status === 'ready' && sessions.length === 0 && (
            <EmptyState title="No Business Discoveries yet">
              Create a client link above to start the first one.
            </EmptyState>
          )}
          {status === 'ready' && sessions.length > 0 && (
            <div className="nsc-table-wrap">
              <table className="nsc-table">
                <thead>
                  <tr>
                    <th scope="col">Company</th>
                    <th scope="col">Client</th>
                    <th scope="col">Status</th>
                    <th scope="col">Progress</th>
                    <th scope="col">Created</th>
                    <th scope="col"><span className="nsc-sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((session) => {
                    const discovery = discoveries[session.session_id];
                    const progress = progressOf(discovery, session.current_layer);
                    const detailPath = `/internal/sessions/${session.session_id}/discovery`;
                    return (
                      <tr key={session.session_id}>
                        <td data-label="Company">
                          <Link to={detailPath} className="nsc-table__primary">
                            <Value value={companyOf(session, discovery)} />
                          </Link>
                          <span className="nsc-table__secondary nsc-mono">{session.session_id}</span>
                        </td>
                        <td data-label="Client"><Value value={clientOf(session)} /></td>
                        <td data-label="Status"><StatusBadge lifecycle={session.lifecycle} /></td>
                        <td data-label="Progress">{discovery === undefined ? '…' : progress.label}</td>
                        <td data-label="Created">{formatDateTime(session.created_at)}</td>
                        <td data-label="Actions" className="nsc-table__actions">
                          <Link to={detailPath} className="nsc-link">View</Link>
                          <CopyLinkButton path={session.assessment_path} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <DiscoveryPyramid />
      </div>
    </ConsoleLayout>
  );
}
